import type { UpdateContactInput } from 'app/generated/gql/graphql'
import { observer } from 'mobx-react-lite'
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ClipboardCheck } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { FollowUpForm } from 'app/components/activities/FollowUpForm'
import { CallInitiationModal } from 'app/components/click-to-call/CallInitiationModal'
import { OutboundCallProvider } from 'app/components/click-to-call/OutboundCallProvider'
import { ClickToCallButton } from 'app/components/click-to-call/ClickToCallButton'
import { ContactActions } from 'app/components/contacts/ContactActions'
import { ContactCustomization } from 'app/components/contacts/ContactCustomization'
import { ContactNameEditor } from 'app/components/contacts/drawer/ContactNameEditor'
import { QuickCreateTaskModal } from 'app/components/tasks/QuickCreateTaskModal'
import { Tooltip } from 'app/components/Tooltip'
import { getAge } from 'app/date.utils'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { isLocationIntegrated, useUser } from 'app/utils'
import { logError, trace } from 'lib/logging'

type Props = {
  isExpanded?: boolean
  contact: ContactJson
  location: LocationJson
  onChange: (contact: ContactJson) => void
  onSaved?: () => void
  onActivityAdded?: (activity: ActivityJson) => void
  isAgentOnly?: boolean
}

const getTaskForm = (contact, onActivityAdded) => (
  <QuickCreateTaskModal
    onTaskCreatedOrUpdated={(task) => {
      onActivityAdded?.({
        id: task!.id,
        activityDate: task!.createdDate,
        type: `Task`,
        data: { dueDate: task!.dueDate, status: task!.status },
      } as ActivityJson)
    }}
    defaultContact={contact}
  />
)

const getFollowUpForm = (contact, onActivityAdded) => (
  <FollowUpForm
    contact={contact!}
    onSubmit={(_activity, followUpResult) => {
      if (followUpResult) {
        onActivityAdded?.({
          id: followUpResult.id,
          type: `FollowUp`,
          typeId: followUpResult.id,
          activityDate: followUpResult.followUpDate ?? new Date().toISOString(),
          description: followUpResult.description,
          owner: followUpResult.owner,
        })
      }
    }}
    onComplete={closeModal}
  />
)

// observer: reads authStore user and contact store state in render, which are MobX state.
const ContactBasicActions = observer(function ContactBasicActions({
  isExpanded = true,
  contact,
  location,
  onChange,
  onSaved,
  onActivityAdded,
  isAgentOnly = false,
}: Props): ReactElement {
  const user = useUser()
  const isMobile = useIsMobile()
  // On mobile the name sits full-width on its own line with the actions below it,
  // regardless of the desktop expand/narrow state.
  const stacked = isMobile || !isExpanded
  const contactStore = useContactStore()
  const isIntegrated = isLocationIntegrated(location)
  const isMarkedNonPatient = contact.patientStatus === `NonPatient`
  const [contactSaving, setContactSaving] = useState(false)

  if (!user) return <></>

  const updateContact = async (data: UpdateContactInput) => {
    trace(`contacts`, `updateContact from ContactBasicCardV2`, data)
    setContactSaving(true)
    try {
      const updateResult = await contactStore.update(contact.id!, data)
      if (updateResult.error) {
        logError(`Error updating contact from ContactBasicCardV2`, updateResult.error.message)
        notifyError(`Error updating contact`)
        return
      }
      const result = contactStore.current ?? ((updateResult.data as any)?.updateContact as ContactJson | undefined)
      if (result) {
        notifySuccess(`Save Successful`)
        onSaved?.()
      }
    } finally {
      setContactSaving(false)
    }
  }

  const handleMarkNonPatient = async () => {
    const nonPatientData = {
      patientStatus: `NonPatient`,
    }
    await updateContact(nonPatientData)
  }

  const handleMarkPatient = async (contactData: UpdateContactInput) => {
    await updateContact(contactData)
  }

  const handleMarkLead = async () => {
    const leadData = {
      lifecycleStage: `Qualified Lead`,
      leadStatus: `In Progress`,
    }
    await updateContact(leadData)
  }

  const handleCustomizeDrawer = (): void => {
    showModal(
      <ContactCustomization
        onConfigSaved={() => {
          // no-op: store updates reactively
        }}
        isIntegrated={isIntegrated}
        contact={contact}
        user={user}
      />,
      {
        title: `Customize contact drawer`,
        isFullScreen: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const handleActivityAdded = (activity: ActivityJson) => {
    onActivityAdded?.(activity)
    // Check if follow-up updated the contact and trigger onChange if needed
    if (activity.type === `FollowUp` && contact) {
      const urlDrawerParam = new URL(window.location.href).searchParams.get(`drawer`)
      if (urlDrawerParam === contact.id) {
        onChange?.(contact)
      }
    }
  }

  const handleClickToCall = () => {
    if (!location?.primaryTrackingNumber) {
      notifyError(
        `Outbound calls cannot be made for this location because it is missing a primary tracking number. Please configure it in Location Settings > Phone Numbers.`,
      )
      return
    }
    showModal(
      <OutboundCallProvider>
        <CallInitiationModal contact={contact!} onContactRefresh={() => onChange(contact)} />
      </OutboundCallProvider>,
      {
        title: `Outbound Call`,
        isFullScreen: false,
        removePadding: true,
        closable: true,
        showMinimizeButton: false,
        useRadix: true,
      },
    )
  }

  const handleAddFollowUp = () => {
    showModal(getFollowUpForm(contact, handleActivityAdded), {
      title: `Add Follow Up`,
      isFullScreen: false,
      overflowHidden: false,
      removePadding: true,
      useRadix: true,
    })
  }

  const handleAddTask = () => {
    showModal(getTaskForm(contact, handleActivityAdded), {
      // Same options as the tasks page: QuickCreateTaskModal renders its own
      // title input as the header (plus its own close X), so the Modal chrome
      // must not stack another header on top.
      hideTitle: true,
      isFullScreen: false,
      overflowHidden: false,
      removePadding: true,
      useRadix: true,
    })
  }

  return (
    <div
      className={classNames(`flex`, stacked ? `flex-col items-start gap-y-4` : `flex-row items-center justify-between`)}
    >
      {/* w-full only when stacked: in the desktop row layout it would eat the
          whole line and squeeze the actions into a second row. */}
      <h3 className={classNames(`gilroy flex items-center text-strong`, stacked && `w-full`)}>
        <ContactNameEditor
          key={contact.id}
          name={contact.name}
          nameSuffix={contact.birthDate && <span className="phi">{`, ${getAge(contact.birthDate)}`}</span>}
          isLocked={!!contact.patientIds?.length}
          isReadOnly={isAgentOnly}
          onRename={(name) => updateContact({ name })}
        />
      </h3>
      {!isAgentOnly && (
        // Wrap only when stacked; on desktop shrink-0 keeps the actions on one row.
        <div className={classNames(`flex items-center gap-2`, stacked ? `flex-wrap` : `shrink-0`)}>
          <ClickToCallButton contact={contact!} onClick={handleClickToCall} iconOnly={isMobile} />
          <Button variant="tertiary" size="sm" onClick={handleAddFollowUp} aria-label="Follow Up">
            <ArrowUturnLeftIcon className="h-4 w-4" />
            {!isMobile && <span>Follow Up</span>}
          </Button>
          {user.workspaceId && (
            <Button variant="tertiary" size="sm" onClick={handleAddTask} aria-label="Task">
              <ClipboardCheck className="h-4 w-4" />
              {!isMobile && <span>Task</span>}
            </Button>
          )}
          <Tooltip message="Contact Actions">
            <ContactActions
              contact={contact}
              location={location}
              isIntegrated={isIntegrated}
              disabled={isMarkedNonPatient || contactSaving}
              onMarkNonPatient={handleMarkNonPatient}
              onMarkPatient={handleMarkPatient}
              onMarkLead={handleMarkLead}
              onChange={onChange}
              onCustomizeDrawer={handleCustomizeDrawer}
              onActivityAdded={onActivityAdded}
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
})

export default ContactBasicActions
